import { InstituteDetails, User } from '@admin/api/apiTypes';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { WIDGET_COLORS } from './overviewWidgets/WidgetText';
import {
  SegmentFilter,
  segmentFilterKey,
  segmentFilterLabel,
  toSegmentRansackParams
} from './segmentFilters';
import { PassportTable, PassportTableFilters, supervisorScopedFilters } from './typedPassportTable';

interface EducatorsTableProps {
  user: User | null;
  institute?: InstituteDetails | null;
  // The Overview filter bar's filters, which the segment filters narrow further.
  filters?: PassportTableFilters | null;
  segmentFilters: SegmentFilter[];
  onRemoveSegmentFilter: (filter: SegmentFilter) => void;
  onClearSegmentFilters: () => void;
  title?: string;
  showExportButton?: boolean;
}

// The Overview tab's educator table: PassportTable plus the chips for whichever
// widget segments are currently filtering it. The Action Queue tab has no
// segments to filter by and keeps using YourPeopleWidget.
const EducatorsTable = ({
  user,
  institute,
  filters = null,
  segmentFilters,
  onRemoveSegmentFilter,
  onClearSegmentFilters,
  title = 'Educators',
  showExportButton = false
}: EducatorsTableProps) => {
  const combinedFilters = React.useMemo(
    () =>
      supervisorScopedFilters(
        { ...(filters ?? {}), ...toSegmentRansackParams(segmentFilters) },
        user
      ),
    [user, filters, segmentFilters]
  );

  const filterChips =
    segmentFilters.length > 0 ? (
      <>
        {segmentFilters.map((filter) => (
          <Chip
            key={segmentFilterKey(filter)}
            label={segmentFilterLabel(filter)}
            onDelete={() => onRemoveSegmentFilter(filter)}
            // Chip's default delete icon is a filled circle-X; the design uses a
            // plain stroked one.
            deleteIcon={<CloseIcon />}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              borderColor: '#E0E0E0',
              fontSize: 13,
              '& .MuiChip-label': { paddingLeft: '12px', paddingRight: '8px' },
              '& .MuiChip-deleteIcon': {
                fontSize: 18,
                marginRight: '8px',
                marginLeft: 0,
                color: '#1F1F1F',
                '&:hover': { color: WIDGET_COLORS.gray }
              }
            }}
          />
        ))}
        <Typography
          component="a"
          onClick={onClearSegmentFilters}
          sx={{
            color: 'text.primary',
            fontSize: 13,
            opacity: 0.7,
            cursor: 'pointer',
            '&:hover': { opacity: 1 }
          }}
        >
          Clear
        </Typography>
      </>
    ) : null;

  return (
    <PassportTable
      institute={institute}
      title={title}
      filters={combinedFilters}
      showExportButton={showExportButton}
      filterChips={filterChips}
    />
  );
};

export default EducatorsTable;
